/**
 * Search hook with debounce for the DataList component.
 *
 * Manages immediate search input state, a debounced search term for
 * API/filtering, and client-side filtering of data rows against
 * searchable columns.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import type { ToolbarConfig, ColumnConfig } from '../types';
import { DEFAULT_SEARCH_DEBOUNCE_MS } from '../constants';
import { safeString } from '../utils/cellRenderer';

// ---------------------------------------------------------------------------
// Public return type
// ---------------------------------------------------------------------------

export interface UseListSearchReturn {
  /** Immediate input value (updates synchronously on every keystroke). */
  searchTerm: string;

  /** Updates the immediate search term state. */
  setSearchTerm: (term: string) => void;

  /** Debounced value used for API queries or client-side filtering. */
  debouncedSearchTerm: string;

  /**
   * Data after client-side search filtering has been applied.
   * In server mode (or when the search term is empty), this is the
   * original `data` array passed in -- no copying occurs.
   */
  filteredData: Record<string, unknown>[];
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useListSearch(
  config: ToolbarConfig['search'],
  data: Record<string, unknown>[],
  columns: Record<string, ColumnConfig>,
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown,
): UseListSearchReturn {
  // -- immediate search term ------------------------------------------------
  const [searchInput, setSearchInput] = useState('');

  // -- debounced search term ------------------------------------------------
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const debounceMs = config?.debounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchInput);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInput, debounceMs]);

  // -- stable setter --------------------------------------------------------
  const setSearchTerm = useCallback((term: string) => {
    setSearchInput(term);
  }, []);

  // -- resolve searchable column keys (memoised) ----------------------------
  const searchableColumnKeys = useMemo(() => {
    if (config?.searchableColumns && config.searchableColumns.length > 0) {
      return config.searchableColumns;
    }

    // Default: all columns whose type is 'text' or unspecified (undefined).
    return Object.entries(columns)
      .filter(([, col]) => col.type === 'text' || col.type === undefined)
      .map(([key]) => key);
  }, [config?.searchableColumns, columns]);

  // -- ref for getCellValue to avoid re-filtering on identity changes -------
  const getCellValueRef = useRef(getCellValue);
  getCellValueRef.current = getCellValue;

  // -- filtered data --------------------------------------------------------
  const filteredData = useMemo(() => {
    // Nothing to filter: return data as-is.
    if (!debouncedSearchTerm) {
      return data;
    }

    // Server mode: filtering happens on the API side.
    if (config?.mode === 'server') {
      return data;
    }

    const lowerTerm = debouncedSearchTerm.toLowerCase();

    return data.filter((row) =>
      searchableColumnKeys.some((colKey) => {
        const raw = getCellValueRef.current(row, colKey);
        if (raw == null) return false;
        return safeString(raw).toLowerCase().includes(lowerTerm);
      }),
    );
  }, [debouncedSearchTerm, config?.mode, data, searchableColumnKeys]);

  return {
    searchTerm: searchInput,
    setSearchTerm,
    debouncedSearchTerm,
    filteredData,
  };
}
